import { useSearchParams, Link } from "react-router-dom";
import LocalLink from "@/components/ui/LocalLink";
import LuxuryCard from "@/components/ui/LuxuryCard";
import { searchPosts } from "@/utils/blogData";
import { useSEO } from "@/utils/useSEO";
import { useTranslation } from "react-i18next";

export default function NewsSearch() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const query = searchParams.get("q") || "";

  useSEO(`Search results for "${query}"`);

  const posts = query ? searchPosts(query) : [];

  return (
    <>
      {/* Simple Header — no fullscreen hero */}
      <div className="pt-32 pb-12 bg-[#0B0B0B] border-b border-white/5">
        <div className="container mx-auto px-6 max-w-6xl">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-white/40 mb-6">
            <LocalLink to="/news" className="hover:text-gold transition-colors">{t("menu.news", "News")}</LocalLink>
            <span>/</span>
            <span className="text-white/70">{t("newsSearch.searchBreadcrumb", "Search")}</span>
          </div>

          <span className="text-gold uppercase tracking-[0.3em] text-xs font-semibold mb-3 block">{t("newsSearch.searchResults", "Search Results")}</span>
          <h1 className="text-4xl md:text-5xl font-luxury text-white mb-4">
            {query ? `"${query}"` : t("newsSearch.searchArticles", "Search Articles")}
          </h1>
          <p className="text-white/50 text-sm">
            {t("newsSearch.resultsFound", "{{count}} results found", { count: posts.length })}
            {query ? t("newsSearch.forQuery", " for \"{{query}}\"", { query: query }) : ""}
          </p>
        </div>
      </div>

      {/* Search Form + Results */}
      <section className="py-16 bg-[#0B0B0B]">
        <div className="container mx-auto px-6 max-w-6xl">
          {/* Search Bar */}
          <div className="mb-12 max-w-xl">
            <form action="/news/search" method="GET" className="flex">
              <input
                type="text"
                name="q"
                defaultValue={query}
                placeholder={t("newsSearch.placeholder", "Search articles...")}
                className="w-full bg-[#121212] text-white border border-white/10 px-5 py-3 rounded-l-full focus:outline-none focus:border-gold transition-colors text-sm"
              />
              <button
                type="submit"
                className="bg-gold text-black px-6 py-3 rounded-r-full font-semibold hover:bg-gold/90 transition-colors text-sm"
              >
                {t("newsSearch.searchBtn", "Search")}
              </button>
            </form>
          </div>

          {posts.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-white/40 text-lg mb-6">
                {query ? t("newsSearch.noResults", "No articles found matching \"{{query}}\".", { query }) : t("newsSearch.enterKeyword", "Enter a keyword to search articles.")}
              </p>
              <LocalLink to="/news" className="btn-gold px-8 py-3 rounded-full inline-block">
                {t("newsSearch.backToNews", "Back to News")}
              </LocalLink>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              {posts.map((post, index) => (
                <LuxuryCard
                  key={post.id}
                  title={post.title}
                  image={post.image}
                  href={`/news/${post.slug}`}
                  description={post.excerpt}
                  features={[post.category, post.date]}
                  delay={index * 0.1}
                />
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
